import { ConflictReasons } from "./ConflictReasons";
import { groupConflictReasons } from "./groupConflictReasons";
import { useState } from "react";
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Collapse,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { ParameterConflict } from "src/store/api/generatedApi";
import { parameterConflictSummaryKey } from "./parameterConflicts";

interface Props {
  hasError: boolean;
  conflicts: ParameterConflict[];
  isChecking: boolean;
  chefGroupNameById: Record<number, string>;
  limitLabelBySignature: Record<string, string>;
}

export const ConflictWarningBanner = ({
  hasError,
  conflicts,
  isChecking,
  chefGroupNameById,
  limitLabelBySignature,
}: Props) => {
  const { t } = useTenantTranslation();
  const reasonCount = groupConflictReasons(conflicts).length;
  const [expanded, setExpanded] = useState(false);

  if (hasError) {
    return <Alert severity="warning">{t("parameterFeasibilityFailed")}</Alert>;
  }

  if (conflicts.length === 0) {
    return null;
  }

  return (
    <Alert severity="warning">
      <AlertTitle>{t("parameterConflictsTitle")}</AlertTitle>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {t(parameterConflictSummaryKey(reasonCount), {
            count: reasonCount,
          })}
        </Typography>
        {isChecking ? <CircularProgress size={16} color="inherit" /> : null}
        <Link
          component="button"
          type="button"
          variant="body2"
          color="inherit"
          underline="hover"
          onClick={() => {
            setExpanded((current) => !current);
          }}
          sx={{ whiteSpace: "nowrap", fontWeight: "fontWeightMedium" }}
        >
          {expanded ? t("hideDetails") : t("showDetails")}
        </Link>
      </Stack>
      <Collapse in={expanded}>
        <Stack
          gap={0.5}
          sx={{ mt: 1, maxHeight: 240, overflowY: "auto", width: "100%" }}
        >
          <ConflictReasons
            conflicts={conflicts}
            chefGroupNameById={chefGroupNameById}
            limitLabelBySignature={limitLabelBySignature}
          />
        </Stack>
      </Collapse>
    </Alert>
  );
};
